// src/utils/numberUtils.js
// utils/numberUtils.js

// NUMERICOS
export const formatNumeric = (value, casasDecimais = 2) => {
    const numero = parseNumeric(value);
    return numero.toLocaleString('pt-BR', {
        minimumFractionDigits: casasDecimais,
        maximumFractionDigits: casasDecimais,
    });
};

export function parseNumeric(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }

    if (typeof value === 'string') {
        const cleaned = value
            .trim()
            .replace(/\s/g, '')
            .replace(/[^\d,.-]/g, '');

        if (!cleaned) return 0;

        const lastComma = cleaned.lastIndexOf(',');
        const lastDot = cleaned.lastIndexOf('.');
        let normalized = cleaned;

        if (lastComma > -1 && lastDot > -1) {
            // Quando tem os dois separadores, o ultimo e considerado decimal.
            if (lastComma > lastDot) {
                normalized = cleaned.replace(/\./g, '').replace(',', '.');
            } else {
                normalized = cleaned.replace(/,/g, '');
            }
        } else if (lastComma > -1) {
            normalized = cleaned.replace(/\./g, '').replace(',', '.');
        } else if (lastDot > -1) {
            const dotCount = (cleaned.match(/\./g) || []).length;
            const decimalPartLength = cleaned.length - lastDot - 1;
            const isLikelyThousands = dotCount > 1 || (dotCount === 1 && decimalPartLength === 3);
            normalized = isLikelyThousands ? cleaned.replace(/\./g, '') : cleaned;
        }

        value = normalized;
    }

    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
}

/**
 * Converte um valor monetário para centavos inteiros. Todas as regras de venda
 * devem calcular e comparar dinheiro nessa unidade para não propagar resíduos
 * binários do JavaScript, como 92.97999999999999.
 */
export function toMoneyCents(value) {
    const parsed = parseNumeric(value);
    if (!Number.isFinite(parsed)) {
        return 0;
    }

    const ajuste = parsed === 0 ? 0 : Math.sign(parsed) * Number.EPSILON;
    return Math.round((parsed + ajuste) * 100);
}

export function fromMoneyCents(value) {
    const cents = Number(value);
    return Number.isFinite(cents) ? Math.trunc(cents) / 100 : 0;
}

export function normalizeMoney(value) {
    return fromMoneyCents(toMoneyCents(value));
}

export function sumMoney(values = []) {
    return fromMoneyCents(values.reduce((total, value) => total + toMoneyCents(value), 0));
}

export function roundDecimal(value, casasDecimais = 10) {
    const parsed = parseNumeric(value);
    if (!Number.isFinite(parsed)) {
        return 0;
    }

    const fator = 10 ** casasDecimais;
    const ajuste = parsed === 0 ? 0 : Math.sign(parsed) * Number.EPSILON;
    return Math.round((parsed + ajuste) * fator) / fator;
}

export function normalizeDecimalInputBr(value, casasDecimais = 2) {
    if (value === null || value === undefined) {
        return '';
    }

    const raw = String(value);
    if (!raw.trim()) {
        return '';
    }

    const cleanedRaw = raw.replace(/[^\d,.-]/g, '');
    // Com vírgula decimal, os pontos pertencem ao agrupamento de milhares.
    const normalizedRaw = cleanedRaw.includes(',')
        ? cleanedRaw.replace(/\./g, '')
        : cleanedRaw.replace(/\./g, ',');
    const negative = normalizedRaw.startsWith('-') ? '-' : '';
    const unsigned = normalizedRaw.replace(/-/g, '');
    const [intPartRaw, ...decimalParts] = unsigned.split(',');
    const intPart = intPartRaw.replace(/\D/g, '');

    if (decimalParts.length === 0 || casasDecimais === 0) {
        return `${negative}${intPart}`;
    }

    const decimalRaw = decimalParts.join('').replace(/\D/g, '');
    const decimal = casasDecimais >= 0 ? decimalRaw.slice(0, casasDecimais) : decimalRaw;

    return `${negative}${intPart || '0'},${decimal}`;
}

// MONETARIOS
export function formatCurrency(value) {
    if (value === null || value === undefined || value === '') {
        return 'R$ 0,00';
    }

    let parsedValue = value;

    if (typeof parsedValue === 'string') {
        // Suporta valores como "1234,56", "1.234,56" e "1234.56"
        const raw = parsedValue.trim();
        const normalized = raw.includes(',')
            ? raw.replace(/\./g, '').replace(',', '.')
            : raw;
        parsedValue = parseFloat(normalized);
    }

    const numericValue = Number(parsedValue);
    if (Number.isNaN(numericValue)) {
        return 'R$ 0,00';
    }

    const formattedValue = numericValue
        .toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `R$ ${formattedValue}`;
}

export function parseCurrency(value) {
    if (typeof value === 'string') {
        value = value.replace('R$ ', '').replace(/\./g, '').replace(',', '.');
    }
    return parseFloat(value) || 0;
}
